import React, { useState, useMemo } from 'react';
import './App.css';
import dataWarehouse from './data/dataWarehouse.json';
import FilterSection from './components/FilterSection';
import SummaryCard from './components/SummaryCard';
import DateRangePicker from './components/DateRangePicker';
import CaseTypePieChart from './components/CaseTypePieChart';
import BusinessUnitPieChart from './components/BusinessUnitPieChart';
import PriorityBarChart from './components/PriorityBarChart';

interface FilterState {
  selectedBUs: string[];
  selectedRegions: string[];
  startDate: string;
  endDate: string;
}

// Helper functions to work with data warehouse
const getBusinessUnitByCode = (code: string) => 
  dataWarehouse.dimensions.businessUnits.find(bu => bu.buCode === code);

const getRegionByCode = (code: string) => 
  dataWarehouse.dimensions.regions.find(r => r.regionCode === code);

const getCaseTypeById = (id: number) => 
  dataWarehouse.dimensions.caseTypes.find(ct => ct.caseTypeId === id);

const getPriorityById = (id: number) => 
  dataWarehouse.dimensions.priorities.find(p => p.priorityId === id);

const getBusinessUnitById = (id: number) => 
  dataWarehouse.dimensions.businessUnits.find(bu => bu.buId === id);


function App() {
  const [filters, setFilters] = useState<FilterState>({
    selectedBUs: [],
    selectedRegions: [],
    startDate: '',
    endDate: ''
  });

  // Get dimension lists for filters
  const businessUnitCodes = dataWarehouse.dimensions.businessUnits.map(bu => bu.buCode);
  const regionCodes = dataWarehouse.dimensions.regions.map(r => r.regionCode);

  const filteredData = useMemo(() => {
    let filtered = dataWarehouse.facts.cases.filter(fact => {
      // Only show active cases by default
      return fact.status === 'Active';
    });

    // Filter by business units
    if (filters.selectedBUs.length > 0) {
      const selectedBUIds = filters.selectedBUs.map(code => {
        const bu = getBusinessUnitByCode(code);
        return bu ? bu.buId : null;
      }).filter(Boolean);
      
      filtered = filtered.filter(fact => selectedBUIds.includes(fact.buId));
    }

    // Filter by regions
    if (filters.selectedRegions.length > 0) {
      const selectedRegionIds = filters.selectedRegions.map(code => {
        const region = getRegionByCode(code);
        return region ? region.regionId : null;
      }).filter(Boolean);
      
      filtered = filtered.filter(fact => selectedRegionIds.includes(fact.regionId));
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(fact => fact.dateKey >= filters.startDate);
    }

    if (filters.endDate) {
      filtered = filtered.filter(fact => fact.dateKey <= filters.endDate);
    }

    return filtered;
  }, [filters]);

  const chartData = useMemo(() => {
    // Calculate case type distribution from filtered data
    const caseTypeDistribution = filteredData.reduce((acc, fact) => {
      const caseType = getCaseTypeById(fact.caseTypeId);
      if (caseType) {
        acc[caseType.caseType] = (acc[caseType.caseType] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Calculate business unit distribution from filtered data
    const businessUnitDistribution = filteredData.reduce((acc, fact) => {
      const bu = getBusinessUnitById(fact.buId);
      if (bu) {
        acc[bu.buCode] = (acc[bu.buCode] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Calculate priority breakdown by business unit from filtered data
    const priorityByBusinessUnit = filteredData.reduce((acc, fact) => {
      const bu = getBusinessUnitById(fact.buId);
      const priority = getPriorityById(fact.priorityId);
      
      if (bu && priority) {
        if (!acc[bu.buCode]) {
          acc[bu.buCode] = { Top: 0, High: 0, Medium: 0, Low: 0, Non: 0 };
        }
        acc[bu.buCode][priority.priority as keyof typeof acc[string]] += 1;
      }
      return acc;
    }, {} as Record<string, { Top: number; High: number; Medium: number; Low: number; Non: number }>);

    // Calculate priority breakdown for summary
    const priorityBreakdown = filteredData.reduce((acc, fact) => {
      const priority = getPriorityById(fact.priorityId);
      if (priority) {
        acc[priority.priority as keyof typeof acc] += 1;
      }
      return acc;
    }, { Top: 0, High: 0, Medium: 0, Low: 0, Non: 0 });

    return {
      caseTypeDistribution,
      businessUnitDistribution,
      priorityByBusinessUnit,
      priorityBreakdown
    };
  }, [filteredData]);

  const updateFilters = (newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearAllFilters = () => {
    setFilters({
      selectedBUs: [],
      selectedRegions: [],
      startDate: '',
      endDate: ''
    });
  };

  return (
    <div className="App">
      <div className="dashboard-container">
        <h1 className="dashboard-title">Integrated Land Enforcement System - HKSAR</h1>
        
        <FilterSection
          businessUnits={businessUnitCodes}
          regions={regionCodes}
          selectedBUs={filters.selectedBUs}
          selectedRegions={filters.selectedRegions}
          onBUChange={(selectedBUs) => updateFilters({ selectedBUs })}
          onRegionChange={(selectedRegions) => updateFilters({ selectedRegions })}
          onClearAll={clearAllFilters}
        />
        
        <SummaryCard
          totalCases={filteredData.length}
          priorityBreakdown={chartData.priorityBreakdown}
        />
        
        <DateRangePicker
          startDate={filters.startDate}
          endDate={filters.endDate}
          onDateChange={(startDate, endDate) => updateFilters({ startDate, endDate })}
        />
        
        <div className="charts-container">
          <div className="charts-row">
            <div className="chart-column">
              <CaseTypePieChart data={chartData.caseTypeDistribution} />
              <BusinessUnitPieChart data={chartData.businessUnitDistribution} />
            </div>
            <div className="chart-column">
              <PriorityBarChart data={chartData.priorityByBusinessUnit} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
